import type { CollabAutomationStatus } from "@unbox/api";
import { Send01 } from "@untitledui/icons";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useCollabTriggerTypes } from "@/pages/sequences/collab-trigger-types";

type AutomationsActiveSignalProps = {
  automations: CollabAutomationStatus[];
  variant: "pill" | "inline";
  onOpen: () => void;
};

export const useActiveAutomationsCount = (automations: CollabAutomationStatus[]) => {
  const triggerTypes = useCollabTriggerTypes();
  return automations.filter((automation) => automation.enabled && triggerTypes.includes(automation.triggerType)).length;
};

export const AutomationsActiveSignal = ({ automations, variant, onOpen }: AutomationsActiveSignalProps) => {
  const count = useActiveAutomationsCount(automations);
  if (count === 0) return null;

  const label = `${count} Active`;

  return (
    <HoverCard openDelay={100} closeDelay={150}>
      <HoverCardTrigger asChild>
        {variant === "pill" ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpen();
            }}
            className="inline-flex h-[21px] shrink-0 cursor-pointer items-center gap-1 rounded-md border border-utility-blue-light-200 bg-utility-blue-light-50 py-0.5 pr-2 pl-1.5"
          >
            <Send01 className="size-3 text-utility-blue-light-700" />
            <span className="text-xs font-medium text-utility-blue-light-700">{label}</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm"
          >
            <Send01 className="size-5 text-purple-950" />
            <span className="flex items-center gap-1">
              <span className="text-quaternary">Automations</span>
              <span className="font-medium text-brand-primary underline underline-offset-2">{label}</span>
            </span>
          </button>
        )}
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        onClick={(event) => event.stopPropagation()}
        className="w-auto rounded-lg border-0 bg-primary-solid px-3 py-2 text-xs font-semibold text-white"
      >
        {count}{" "}
        <button type="button" onClick={onOpen} className="cursor-pointer underline underline-offset-2">
          {count === 1 ? "Automation" : "Automations"}
        </button>{" "}
        Running
      </HoverCardContent>
    </HoverCard>
  );
};
